/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, ChangeEvent, useCallback, useState, useMemo, useEffect } from 'react';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';
import { useQuery } from '@tanstack/react-query';

import { useFetchErrorController } from '@/controllers';
import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';

import * as stylesSearch from './search-layouts/styles';
import SearchBox from './search-layouts/search-box';
import SearchStatusBar from './search-layouts/search-status-bar';
import SearchActionBar from './search-layouts/search-action-bar';
import SearchRefiner from './search-layouts/search-refiner';
import SearchResults from './search-results/index';
import { testIds } from './constants';
import { RefineOptionKeyType, URLObjectType } from './types';
import { reviseFilterOptions, getDisabledOptions } from './utils';

const Search: FC = () => {
  const [searchQuerySpec, setSearchQuerySpec] = useState<{
    path: string;
    queryKey: string[];
  }>({
    path: '',
    queryKey: [],
  });
  const [disabledOptions, setDisabledOptions] = useState<RefineOptionKeyType[]>([]);

  const { onError } = useFetchErrorController();
  const [, setQuery] = useQueryParams({});

  const { search } = useLocation();
  const { urlObject, searchTerm, currentPage, urlObjectStr, isSearchEnabled } = useMemo(() => {
    const urlObjectMemo: URLObjectType = qs.parse(search) as URLObjectType;
    const searchTermMemo: string = (urlObjectMemo.query as string) || '';
    const currentPageMemo: number = Number(urlObjectMemo.page as string) || 1;
    // The reason to use string rather than url object for useEffect deps
    // checking is because: there is a query fetch in useEffect listener, if
    // useMemo of urlObject is accidently removed or urlObject somehow failed to
    // be memorised, FE could be sending infinity query calls to API, to use
    // urlObjectStr can avoid this from happening.
    const urlObjectStrMemo: string = qs.stringify(urlObjectMemo);

    return {
      urlObject: urlObjectMemo,
      searchTerm: searchTermMemo,
      currentPage: currentPageMemo,
      urlObjectStr: urlObjectStrMemo,
      isSearchEnabled: urlObjectStrMemo.length > 0,
    };
  }, [search]);

  const handleFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = event.currentTarget as {
        value: RefineOptionKeyType;
        checked: boolean;
      };
      const { query, disabledOptionKeys } = reviseFilterOptions(value, checked, urlObject);
      setDisabledOptions(disabledOptionKeys);
      setQuery(query);
    },
    [urlObjectStr]
  );

  const handleInputChange = useCallback((value: string) => {
    setQuery({ query: value || undefined, page: undefined });
  }, []);

  const handlePageChange = (_: any, page: number) => {
    setQuery({ page: page > 1 ? page : undefined });
  };

  const { path, queryKey } = searchQuerySpec;
  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    enabled: queryKey.length > 0 && path.length > 0 && isSearchEnabled,
    onError,
    staleTime: 3600 * 1000, // 1 hour for now
  });

  // Only triggered when url obj string changes
  useEffect(() => {
    const { query, page = 1, ...rest } = urlObject;
    const spec = querySpec('search.{search}', {
      search: `page=${Number(page) - 1}&query=${encodeURIComponent(query || '')}&${qs.stringify(
        rest
      )}`,
    });
    setSearchQuerySpec(spec);
  }, [urlObjectStr]);

  // Only triggered once in first page load
  useEffect(() => {
    setDisabledOptions(getDisabledOptions(urlObject));
  }, []);

  return (
    <div data-testid={testIds.advancedSearch}>
      <SearchStatusBar isLoading={isLoading} total={data?.totalElements} />
      <div css={stylesSearch.searchMainStyles}>
        <SearchRefiner
          urlObject={urlObject}
          disabledOptions={disabledOptions}
          onFilterChange={handleFilterChange}
        />
        <div css={stylesSearch.searchContentStyles}>
          <SearchBox isLoading={isLoading} inputValue={searchTerm} onChange={handleInputChange} />
          <SearchResults isLoading={isLoading} data={data} isSearchEnabled={isSearchEnabled} />
          {isSearchEnabled && (
            <SearchActionBar
              data={data}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
